import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ShieldCheck, ArrowLeft, Smartphone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const GoogleIcon = () => (
  <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 4.1 29.6 2 24 2 12 2 2 12 2 24s10 22 22 22 22-10 22-22c0-1.3-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 4.1 29.6 2 24 2 15.7 2 8.6 6.6 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 46c5.5 0 10.4-2.1 14.1-5.5l-6.5-5.5c-2 1.5-4.7 2.5-7.6 2.5-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C8.5 41.3 15.6 46 24 46z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.5 5.5C41.4 36.6 46 31 46 24c0-1.3-.1-2.3-.4-3.5z"/>
  </svg>
);

export default function Login() {
  const [step, setStep] = useState("credentials"); // credentials | enroll | mfa
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [qr, setQr] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleProcessing, setGoogleProcessing] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      if (sid) completeGoogle(sid);
    }
    // eslint-disable-next-line
  }, []);

  const completeGoogle = async (sessionId) => {
    setGoogleProcessing(true);
    try {
      const { data } = await api.post("/auth/google/session", { session_id: sessionId });
      window.history.replaceState(null, "", "/login");
      login(data.user);
      toast.success(`Welcome, ${data.user.full_name || data.user.email}`);
      navigate(data.user.role === "admin" ? "/admin/clients" : "/portal/documents");
    } catch (err) {
      window.history.replaceState(null, "", "/login");
      toast.error(err.response?.data?.detail || "Google sign-in failed");
      setGoogleProcessing(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitCreds = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setChallengeId(data.challenge_id);
      if (data.step === "mfa_enroll_required") {
        setQr(data.qr_png);
        setStep("enroll");
      } else {
        setStep("mfa");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid email or password");
    } finally { setLoading(false); }
  };

  const submitCode = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { challenge_id: challengeId, code });
      login(data.user);
      toast.success(`Welcome, ${data.user.full_name || data.user.email}`);
      navigate(data.user.role === "admin" ? "/admin/clients" : "/portal/documents");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid authentication code");
      setCode("");
    } finally { setLoading(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-mfa", { email, password });
      toast.success("Authenticator reset. Sign in to set up a new one.");
      setCode(""); setStep("credentials");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full bg-[#0d0d0d] border border-white/12 rounded-lg px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent transition-all";

  if (googleProcessing) {
    return (
      <div className="relative min-h-screen bg-[#050505] flex flex-col items-center justify-center gap-5" data-testid="google-processing">
        <div className="noise" />
        <Logo size="lg" stacked />
        <div className="flex items-center gap-3 text-zinc-400 text-sm mt-4">
          <Loader2 size={18} className="animate-spin" /> Completing secure sign-in…
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#050505] flex items-center justify-center px-6 overflow-hidden">
      <div className="noise" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-white/[0.03] rounded-full blur-[120px]" />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16,1,0.3,1] }}
        className="relative z-10 w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo size="lg" stacked /></div>
        <div className="rounded-2xl border border-white/10 bg-[#0b0b0b] p-8">
          <div className="flex items-center gap-2 text-zinc-400 text-sm mb-6">
            <Lock size={15} strokeWidth={1.5} /> Secure Client Portal
          </div>

          {step === "credentials" && (
            <div className="space-y-5">
              <form onSubmit={submitCreds} data-testid="login-form" className="space-y-4">
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider">Email</label>
                  <input required type="email" data-testid="login-email" className={`${inputCls} mt-2`} placeholder="you@firm.co.uk" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider">Password</label>
                  <input required type="password" data-testid="login-password" className={`${inputCls} mt-2`} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                <button type="submit" disabled={loading} data-testid="login-submit" className="btn-metal w-full py-3.5 rounded-lg font-medium text-sm disabled:opacity-60">
                  {loading ? "Verifying…" : "Continue"}
                </button>
              </form>
              <div className="flex items-center gap-4">
                <div className="h-px bg-white/10 flex-1" />
                <span className="text-xs text-zinc-600 uppercase tracking-wider">or</span>
                <div className="h-px bg-white/10 flex-1" />
              </div>
              <button type="button" onClick={googleLogin} data-testid="google-login-btn"
                className="w-full py-3.5 rounded-lg border border-white/15 bg-white/[0.03] text-zinc-200 hover:bg-white/[0.07] hover:border-white/30 transition-all text-sm font-medium flex items-center justify-center gap-3">
                <GoogleIcon /> Continue with Google
              </button>
              <p className="text-xs text-zinc-600 text-center">Google sign-in is available to accounts issued by Tactis.</p>
            </div>
          )}

          {step === "enroll" && (
            <div data-testid="mfa-enroll" className="space-y-5">
              <div className="flex items-start gap-3 text-sm text-zinc-400">
                <Smartphone size={18} strokeWidth={1.5} className="shrink-0 mt-0.5" />
                <p>Set up multi-factor authentication. Scan this code with Google Authenticator, Authy or a similar app, then enter the 6-digit code.</p>
              </div>
              <div className="bg-white p-3 rounded-xl w-fit mx-auto">
                <img src={qr} alt="MFA QR code" className="w-44 h-44" data-testid="mfa-qr" />
              </div>
              <form onSubmit={submitCode} className="space-y-4">
                <div className="flex justify-center" data-testid="mfa-otp-input">
                  <InputOTP maxLength={6} value={code} onChange={setCode}>
                    <InputOTPGroup>
                      {[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} className="border-white/20 text-white" />)}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <button type="submit" disabled={loading || code.length !== 6} data-testid="mfa-verify-enroll" className="btn-metal w-full py-3.5 rounded-lg font-medium text-sm disabled:opacity-50">
                  {loading ? "Verifying…" : "Verify & Sign In"}
                </button>
              </form>
            </div>
          )}

          {step === "mfa" && (
            <div data-testid="mfa-verify" className="space-y-5">
              <div className="flex items-center gap-3 text-sm text-zinc-400">
                <ShieldCheck size={18} strokeWidth={1.5} /> Enter the 6-digit code from your authenticator app.
              </div>
              <form onSubmit={submitCode} className="space-y-4">
                <div className="flex justify-center" data-testid="mfa-otp-input">
                  <InputOTP maxLength={6} value={code} onChange={setCode}>
                    <InputOTPGroup>
                      {[0,1,2,3,4,5].map((i) => <InputOTPSlot key={i} index={i} className="border-white/20 text-white" />)}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <button type="submit" disabled={loading || code.length !== 6} data-testid="mfa-verify-login" className="btn-metal w-full py-3.5 rounded-lg font-medium text-sm disabled:opacity-50">
                  {loading ? "Verifying…" : "Verify & Sign In"}
                </button>
              </form>
              <button type="button" onClick={() => { setCode(""); setStep("reset"); }} className="text-zinc-500 hover:text-white text-xs transition-colors" data-testid="mfa-lost">
                Lost access to your authenticator?
              </button>
            </div>
          )}

          {step === "reset" && (
            <div data-testid="mfa-reset" className="space-y-4">
              <div className="flex items-start gap-3 text-sm text-zinc-400">
                <ShieldCheck size={18} strokeWidth={1.5} className="shrink-0 mt-0.5" />
                <p>Reset your authenticator (admin account). Confirm your email and password — you'll then scan a fresh QR code on your next sign-in.</p>
              </div>
              <form onSubmit={submitReset} className="space-y-4">
                <input required type="email" data-testid="reset-email" className={inputCls} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
                <input required type="password" data-testid="reset-password" className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
                <button type="submit" disabled={loading} data-testid="reset-submit" className="btn-metal w-full py-3.5 rounded-lg font-medium text-sm disabled:opacity-60">
                  {loading ? "Resetting…" : "Reset authenticator"}
                </button>
              </form>
            </div>
          )}

          {step !== "credentials" && (
            <button onClick={() => { setStep("credentials"); setCode(""); }} className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm mt-6 transition-colors" data-testid="mfa-back">
              <ArrowLeft size={14} /> Back
            </button>
          )}
        </div>
        <p className="text-center text-zinc-600 text-xs mt-6">Protected by multi-factor authentication. Accounts are issued by Tactis.</p>
      </motion.div>
    </div>
  );
}
